var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/hooks/useAuth.ts
import { useState, useEffect } from "react";
import { debounce } from "debounce";
import { StandaloneAuth as StandaloneAuth2, GenericError } from "@leagueplatform/auth-standalone";

// src/checkAuth.ts
import { StandaloneAuth } from "@leagueplatform/auth-standalone";
import { attachCookiesWithAccessToken } from "@leagueplatform/auth";
import { getInitializationConfig } from "@leagueplatform/core";
function getAuth0Error() {
  const searchParams = new URLSearchParams(window.location.search);
  const errorType = searchParams.get("error");
  const errorDescription = searchParams.get("error_description");
  if (errorType) {
    return { type: errorType, description: errorDescription };
  }
  return null;
}
function isRedirectFromLogin() {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.has("code") && searchParams.has("state");
}
function removeAuthQueryParams() {
  const url = new URL(window.location.toString());
  url.searchParams.delete("code");
  url.searchParams.delete("state");
  window.history.replaceState(null, "", url.toString());
}
function checkAuth() {
  return __async(this, null, function* () {
    const authenticated = yield StandaloneAuth.client.isAuthenticated();
    if (authenticated) {
      StandaloneAuth.client.getTokenSilently().then((token) => {
        attachCookiesWithAccessToken(getInitializationConfig().contentUrl, token);
      });
      return { isAuthenticated: true };
    }
    const authError = getAuth0Error();
    if (authError) {
      return { isAuthenticated: false, error: authError };
    }
    if (isRedirectFromLogin()) {
      const { appState } = yield StandaloneAuth.client.handleRedirectCallback();
      removeAuthQueryParams();
      const isAuthenticated = yield StandaloneAuth.client.isAuthenticated();
      if (isAuthenticated) {
        return {
          isAuthenticated: true,
          appState
        };
      }
    }
    return { isAuthenticated: false };
  });
}

// src/hooks/useAuth.ts
var CHECK_SESSION_DEBOUNCE_TIMER = 1e3;
function useAuth({
  redirectToLogInWhenSignedOut = false
} = {}) {
  const [authenticated, setAuthenticated] = useState();
  const [error, setError] = useState(null);
  const [appState, setAppState] = useState();
  useEffect(() => {
    const checkSessionValidity = debounce(
      () => __async(this, null, function* () {
        if (!authenticated) {
          return;
        }
        try {
          yield StandaloneAuth2.client.getTokenSilently();
        } catch (err) {
          if (err instanceof GenericError) {
            setError({ type: err.error, description: err.error_description });
          } else if (err instanceof Error) {
            setError({ type: err.name, description: err.message });
          }
          setAuthenticated(false);
        }
      }),
      CHECK_SESSION_DEBOUNCE_TIMER,
      true
    );
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkSessionValidity();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("click", checkSessionValidity);
    document.addEventListener("keydown", checkSessionValidity);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener("click", checkSessionValidity);
      document.removeEventListener("keydown", checkSessionValidity);
    };
  }, [authenticated]);
  useEffect(() => {
    checkAuth().then(
      ({ isAuthenticated, error: authError, appState: returnedAppState }) => {
        if (!isAuthenticated && !authError && redirectToLogInWhenSignedOut) {
          const { pathname, search } = document.location;
          StandaloneAuth2.client.loginWithRedirect({
            appState: { redirectPath: pathname + search }
          });
        }
        if (returnedAppState) {
          setAppState(returnedAppState);
        }
        setAuthenticated(isAuthenticated);
        if (authError) {
          setError(authError);
        }
      }
    );
  }, [redirectToLogInWhenSignedOut]);
  return { isAuthenticated: authenticated, error, appState };
}

// src/util/handle-authentication-for-external-links.util.ts
import { StandaloneAuth as StandaloneAuth3 } from "@leagueplatform/auth-standalone";
import { attachCookiesWithAccessToken as attachCookiesWithAccessToken2 } from "@leagueplatform/auth";
import { getInitializationConfig as getInitializationConfig2 } from "@leagueplatform/core";
function handleAuthenticationForExternalLinks() {
  return __async(this, null, function* () {
    if (StandaloneAuth3.initialized) {
      const { legacyRestApi } = getInitializationConfig2().api;
      if (!legacyRestApi) {
        throw new Error(
          "config.api.legacyRestApi must be specified when calling handleAuthenticationForExternalLinks"
        );
      }
      yield attachCookiesWithAccessToken2(
        getInitializationConfig2().api.legacyRestApi,
        yield StandaloneAuth3.client.getTokenSilently()
      );
    }
  });
}
export {
  handleAuthenticationForExternalLinks,
  useAuth
};
//# sourceMappingURL=index.js.map